import { useState } from "react";
import api, { formatError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useCopy } from "@/contexts/CopyContext";
import { Cake, X, Loader2 } from "lucide-react";

/**
 * OnboardingBirthdayModal — first-login modal:
 * "Thank you for using the Born Day app. I'm adding birthdays to my
 *  calendar. I want to make sure that I never miss another birthday.
 *  Please share yours here."
 *
 * Phone is REQUIRED (light-validated). Year is OPTIONAL with a toggle
 * "Show my birth year". Focus is on month + day.
 */
const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

export default function OnboardingBirthdayModal({ open, onClose, onSaved }) {
  const { user, refresh } = useAuth();
  const { t } = useCopy();
  const [month, setMonth] = useState(user?.birth_month || "");
  const [day, setDay] = useState(user?.birth_day || "");
  const [year, setYear] = useState(user?.birth_year || "");
  const [showYear, setShowYear] = useState(!!user?.show_birth_year);
  const [phone, setPhone] = useState(user?.phone || "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  if (!open) return null;

  const save = async (skip = false) => {
    setBusy(true);
    setErr("");
    try {
      const payload = { has_completed_onboarding: true };
      if (!skip) {
        if (phone && phone.trim()) payload.phone = phone.trim();
        if (month) payload.birth_month = parseInt(month, 10);
        if (day) payload.birth_day = parseInt(day, 10);
        if (year) payload.birth_year = parseInt(year, 10);
        payload.show_birth_year = !!showYear;
      }
      await api.patch("/auth/profile", payload);
      await refresh();
      onSaved && onSaved();
      onClose();
    } catch (e) {
      setErr(formatError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center p-4"
      style={{ background: "rgba(0,0,0,0.5)" }}
      data-testid="onboarding-modal"
    >
      <div className="bd-surface p-6 sm:p-8 w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <span
              className="w-9 h-9 grid place-items-center"
              style={{
                background: "var(--primary)",
                color: "var(--primary-fg)",
                borderRadius: "var(--radius-btn)",
              }}
            >
              <Cake size={16} />
            </span>
            <h2 className="text-2xl font-bold" style={{ letterSpacing: "-0.03em" }}>
              {t("onboarding.headline", "Thank you for using the Born Day app.")}
            </h2>
          </div>
          <button
            onClick={() => save(true)}
            className="bd-btn bd-btn-ghost"
            style={{ padding: "0.4rem" }}
            aria-label="Close"
            data-testid="onboarding-close"
          >
            <X size={14} />
          </button>
        </div>

        <p className="mb-5" style={{ color: "var(--text-dim)" }} data-testid="onboarding-body">
          {t("onboarding.body",
            "I'm adding birthdays to my calendar. I want to make sure that I never miss another birthday. Please share yours here.")}
        </p>

        <div className="grid grid-cols-2 gap-3 mb-4">
          <div>
            <label className="bd-label">Month</label>
            <select
              className="bd-input"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              data-testid="onboarding-month"
            >
              <option value="">Pick a month</option>
              {MONTHS.map((m, i) => (
                <option key={m} value={i + 1}>{m}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="bd-label">Day</label>
            <input
              className="bd-input bd-mono"
              type="number"
              min="1"
              max="31"
              value={day}
              onChange={(e) => setDay(e.target.value)}
              placeholder="e.g. 14"
              data-testid="onboarding-day"
            />
          </div>
        </div>

        <div className="mb-4">
          <label className="bd-label flex items-center gap-2">
            <input
              type="checkbox"
              checked={showYear}
              onChange={(e) => setShowYear(e.target.checked)}
              data-testid="onboarding-show-year-toggle"
            />
            Also share my birth year (off by default — most people focus on the date)
          </label>
          {showYear && (
            <input
              className="bd-input bd-mono mt-2"
              type="number"
              min="1900"
              max={new Date().getFullYear()}
              value={year}
              onChange={(e) => setYear(e.target.value)}
              placeholder="e.g. 1985"
              data-testid="onboarding-year"
            />
          )}
        </div>

        <div className="mb-5">
          <label className="bd-label flex items-center gap-2">
            Phone number
          </label>
          <input
            className="bd-input bd-mono"
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+1 555 123 4567"
            data-testid="onboarding-phone"
          />
          <p className="text-xs mt-1" style={{ color: "var(--text-dim)" }}>
            Light validation only. No text messages will be sent.
          </p>
        </div>

        {err && (
          <div
            className="text-sm px-3 py-2 rounded mb-3"
            style={{ background: "color-mix(in srgb, #ff3b30 15%, transparent)", color: "#ff3b30" }}
            data-testid="onboarding-error"
          >
            {err}
          </div>
        )}

        <div className="flex gap-2 justify-end">
          <button
            className="bd-btn bd-btn-ghost"
            onClick={() => save(true)}
            disabled={busy}
            data-testid="onboarding-skip"
          >
            {t("onboarding.skip_label", "Maybe later")}
          </button>
          <button
            className="bd-btn"
            onClick={() => save(false)}
            disabled={busy}
            data-testid="onboarding-save"
          >
            {busy ? <Loader2 size={16} className="animate-spin" /> : t("onboarding.save_label", "Save my birthday")}
          </button>
        </div>
      </div>
    </div>
  );
}
